// SEQ-192: the one way this app logs an error.
//
// The report widget records console output, and Supabase, fetch and edge
// function errors can carry row values in their text (details, hint, the
// failing payload). This helper logs a short message or code and nothing else:
// never the error object, never a request body, never row data.
//
// Use it wherever a Supabase or fetch error is logged:
//   logError("[Vault] Download failed", err);

const MAX_LENGTH = 200;

// Long digit runs and dollar figures inside a message are masked, so a message
// that quotes a value ("invalid input for amount 1234567.89") stays harmless.
function scrub(text: string): string {
  return text
    .replace(/\$\s*[-+]?\s*\d[\d,]*(?:\.\d+)?/g, "[amount]")
    .replace(/\d{1,3}(?:[, ]\d{3})+(?:\.\d+)?/g, "[n]")
    .replace(/\d[\d,]*\.\d+|\d{4,}/g, (m) => (/^(?:19|20)\d{2}$/.test(m) ? m : "[n]"))
    .slice(0, MAX_LENGTH);
}

// A Supabase or Postgres code (23505, PGRST116, 42P01) is a short token. Anything
// longer or with other characters is not a code, so it is dropped.
function safeCode(code: unknown): string {
  return typeof code === "string" && /^[A-Za-z0-9_-]{1,20}$/.test(code) ? ` (${code})` : "";
}

// Reduces anything thrown to a short, value-free string.
export function safeErrorText(error: unknown): string {
  if (error == null) return "unknown error";
  if (typeof error === "string") return scrub(error);
  if (typeof error === "object") {
    const e = error as { message?: unknown; code?: unknown; status?: unknown; name?: unknown };
    if (typeof e.message === "string" && e.message) {
      const code = safeCode(e.code);
      return scrub(e.message) + code;
    }
    const code = safeCode(e.code);
    if (code) return code.slice(2, -1);
    if (typeof e.status === "number") return `status ${e.status}`;
    if (typeof e.name === "string" && e.name) return scrub(e.name);
  }
  return "unknown error";
}

export function logError(context: string, error?: unknown): void {
  console.error(context, safeErrorText(error));
}

export function logWarn(context: string, error?: unknown): void {
  console.warn(context, safeErrorText(error));
}
